import { Button as AntButton } from "antd"
import { useEffect, useState } from "react"
import { Button as BootstrapButton, Col, Form, Row } from "react-bootstrap"
import { DollarInputV2 } from "./DollarInputV2"
import CheckGroup from "../Common/components/CheckGroup"
import "./styles.css"

const FieldError = ({ message }) => (
    message ? <div className="warning-msg">{message}</div> : null
)

const scrollToField = (fieldKey) => {
    if (!fieldKey || typeof document === "undefined") return
    const el = document.querySelector(`[data-field="${fieldKey}"]`)
    if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "center" })
    }
}

const newKey = () => `${Date.now()}-${Math.random()}`

const updateListItem = (items, index, patch) => (
    items.map((item, itemIndex) => (itemIndex === index ? { ...item, ...patch } : item))
)

const removeListItem = (items, index) => items.filter((_, itemIndex) => itemIndex !== index)

const isManagedMortgageKey = (key) => {
    const value = `${key || ""}`
    return value.startsWith("subject-mortgage:") || value.startsWith("property-mortgage:")
}

const hasAmount = (value) => {
    if (value === "" || value == null) return false
    const parsed = Number(value)
    return !Number.isNaN(parsed)
}

// Match old live Liabilities.js: only alimony/additional expenses (renderExpenses).
// Loan review / "+ Add liability" lived in commented-out renderCore(); credit + Real Estate own that data.
const LiabilitiesV2 = ({ liabilities: initialLiabilities, participant, saving, onSave, onBack }) => {
    const [additionalExpenses, setAdditionalExpenses] = useState([])
    const [fieldErrors, setFieldErrors] = useState({})

    useEffect(() => {
        const fromLiabilities = Array.isArray(initialLiabilities?.additionalExpenses)
            ? initialLiabilities.additionalExpenses
            : null
        const fromIncome = Array.isArray(participant?.income?.alimony)
            ? participant.income.alimony
            : []
        setAdditionalExpenses(fromLiabilities && fromLiabilities.length > 0 ? fromLiabilities : fromIncome)
        setFieldErrors({})
    }, [participant, initialLiabilities])

    const clearFieldError = (key) => {
        setFieldErrors((prev) => {
            if (!prev[key]) return prev
            const next = { ...prev }
            delete next[key]
            return next
        })
    }

    const addAdditionalExpense = () => setAdditionalExpenses((items) => {
        if (items.length >= 3) return items
        return [...items, {
            key: newKey(),
            amount: "",
            owedto: "",
            alimonyorsupport: "alimony",
            lessthan10: null,
            monthstopay: null,
        }]
    })

    const updateAdditionalExpense = (index, patch) => {
        if (Object.prototype.hasOwnProperty.call(patch, "amount")) {
            clearFieldError(`expenses.${index}.amount`)
        }
        setAdditionalExpenses((items) => updateListItem(items, index, patch))
    }

    const validateExpenses = () => {
        const errors = {}
        const order = []
        for (let i = 0; i < additionalExpenses.length; i += 1) {
            const item = additionalExpenses[i]
            if (!hasAmount(item.amount)) {
                const key = `expenses.${i}.amount`
                errors[key] = `Please provide monthly amount for expense #${i + 1}.`
                order.push(key)
            }
        }
        return { errors, firstErrorKey: order[0] || null }
    }

    const handleSave = () => {
        const { errors, firstErrorKey } = validateExpenses()
        if (firstErrorKey) {
            setFieldErrors(errors)
            setTimeout(() => scrollToField(firstErrorKey), 50)
            return
        }
        setFieldErrors({})
        const existingLoans = Array.isArray(initialLiabilities?.loans) ? initialLiabilities.loans : []
        const existingNewMortgages = Array.isArray(initialLiabilities?.newMortgages)
            ? initialLiabilities.newMortgages
            : []
        onSave({
            liabilities: {
                loans: existingLoans.filter((loan) => !isManagedMortgageKey(loan.key)),
                newMortgages: existingNewMortgages,
                additionalExpenses,
            },
        })
    }

    return (
        <>
        <div className="text-left income-application newInterviewPaneProperty application-v2-form-body finance-v2-body">
            <div className="page-header page-header-row">
                <span className="page-header-primary">Finance</span>
                <span className="page-header-separator">/</span>
                <span className="page-header-secondary">Liabilities</span>
            </div>

            <Row className="ml-0 mr-0">
                <Col className="mt-4 pl-0 pr-0" md={8} xs={12}>
                    <Form.Group className="inputSection">
                        <Form.Label>
                            Do you have any additional liabilities and expenses, including alimony, child support, separate maintenance, or job-related expenses?
                        </Form.Label>
                        <CheckGroup
                            className="property-v2-yesno"
                            data={[{ value: "true", label: "Yes" }, { value: "false", label: "No" }]}
                            onChangeSelect={(selected) => {
                                if (selected === "true") {
                                    if (additionalExpenses.length === 0) addAdditionalExpense()
                                } else {
                                    setAdditionalExpenses([])
                                    setFieldErrors({})
                                }
                            }}
                            value={additionalExpenses.length > 0 ? "true" : "false"}
                            type="radio"
                            direction="horizontal"
                            style={{ width: "100%" }}
                        />
                    </Form.Group>
                </Col>
            </Row>

            {additionalExpenses.map((item, index) => {
                const amountErrorKey = `expenses.${index}.amount`
                return (
                    <div className="incard mt-4 pt-0 finance-v2-card finance-v2-expense-card" key={item.key || index}>
                        <Row className="finance-v2-expense-fields">
                            <Col md={4} xs={12} className="pl-0">
                                <Form.Group>
                                    <Form.Label>Payment type</Form.Label>
                                    <Form.Control as="select" value={item.alimonyorsupport || "alimony"} onChange={(e) => updateAdditionalExpense(index, { alimonyorsupport: e.target.value })}>
                                        <option value="alimony">Alimony</option>
                                        <option value="childsupport">Child Support</option>
                                        <option value="maintenancepayments">Separate Maintenance</option>
                                        <option value="jobrelatedexpenses">Job Related Expenses</option>
                                        <option value="other">Other</option>
                                    </Form.Control>
                                </Form.Group>
                            </Col>
                            <Col md={4} xs={12} className="pl-0 pr-0 pl-md-3">
                                <Form.Group data-field={amountErrorKey}>
                                    <Form.Label>*Monthly amount</Form.Label>
                                    <DollarInputV2
                                        value={item.amount || ""}
                                        status={fieldErrors[amountErrorKey] ? "error" : undefined}
                                        onChange={(value) => updateAdditionalExpense(index, { amount: `${value || ""}` })}
                                    />
                                    <FieldError message={fieldErrors[amountErrorKey]} />
                                </Form.Group>
                            </Col>
                        </Row>
                        <div className="finance-v2-card-footer">
                            {additionalExpenses.length < 3 && index === additionalExpenses.length - 1
                                ? <AntButton type="link" className="finance-v2-add-btn" onClick={addAdditionalExpense}>+ Add expense</AntButton>
                                : <span />}
                            <AntButton
                                type="link"
                                danger
                                onClick={() => {
                                    setAdditionalExpenses((items) => removeListItem(items, index))
                                    setFieldErrors({})
                                }}
                            >
                                Remove
                            </AntButton>
                        </div>
                    </div>
                )
            })}

        </div>
        <Row className="text-left application-bottom-bar">
            <div className="d-flex align-items-center">
                <BootstrapButton
                    id="previous"
                    type="button"
                    variant="zeitro-primary"
                    disabled={saving}
                    className="operate-btn back mr-3"
                    onClick={onBack}
                >
                    Back
                </BootstrapButton>
                <BootstrapButton
                    id="next"
                    type="button"
                    variant="zeitro-primary"
                    disabled={saving}
                    className="text-center operate-btn btn"
                    onClick={handleSave}
                >
                        Next
                </BootstrapButton>
            </div>
        </Row>
        </>
    )
}

export default LiabilitiesV2
